import { ctaThemeCssVariables, type CtaTheme } from '@adarveo/shared';
import { useQuery } from '@tanstack/react-query';
import Hls from 'hls.js';
import { ArrowUpRight, CircleAlert, Loader2, Play, Sparkles, Volume2, VolumeX, X } from 'lucide-react';
import { type CSSProperties, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { hasSupabaseConfig, supabase } from '../lib/supabase';
import { trackCollectionEvent } from '../lib/tracking';

type CollectionTarget = {
  itemId: string;
  adId: string;
  campaignId: string;
  campaignName: string;
  targetIndex: number;
  pageNumber: number | null;
  placementLabel: string | null;
  adName: string;
  markerUrl: string | null;
  videoUrl: string | null;
  ctaKicker: string | null;
  ctaHeadline: string | null;
  ctaBody: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  ctaTheme?: CtaTheme | null;
  ctaLogoUrl?: string | null;
};

type CollectionExperience = {
  publicSlug: string;
  name: string;
  status: string;
  mindFileUrl: string | null;
  targetCount: number;
  targets: CollectionTarget[];
};

type BrowserSupport = {
  supported: boolean;
  name: string;
};

type MindARThreeConstructor = new (options: {
  container: HTMLElement;
  imageTargetSrc: string;
  filterMinCF?: number;
  filterBeta?: number;
  uiLoading?: 'yes' | 'no';
  uiScanning?: 'yes' | 'no';
}) => {
  cssRenderer?: {
    domElement: HTMLElement;
  };
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  video?: HTMLVideoElement;
  addAnchor: (targetIndex: number) => {
    group: THREE.Group;
    onTargetFound?: () => void;
    onTargetLost?: () => void;
  };
  start: () => Promise<void>;
  stop: () => Promise<void> | void;
};

type CollectionAnchor = ReturnType<InstanceType<MindARThreeConstructor>['addAnchor']>;

type CollectionArRuntime = {
  anchors: CollectionAnchor[];
  cleanupVideoSource: () => void;
  container: HTMLElement;
  mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  mindarThree: InstanceType<MindARThreeConstructor>;
  renderer: THREE.WebGLRenderer;
  texture: THREE.VideoTexture;
  video: HTMLVideoElement;
};

type PlayContext = 'ar' | 'direct' | 'fallback';

const MINDAR_THREE_MODULE_URL =
  'https://esm.sh/mind-ar@1.2.5/dist/mindar-image-three.prod.js?deps=three@0.160.0';

const ctaDefaults = {
  label: 'Learn more'
};

const AR_START_TIMEOUT_MS = 12000;
const CTA_REVEAL_DELAY_MS = 2400;

const isHlsUrl = (url: string) => /\.m3u8(?:$|[?#])/i.test(url);

const attachVideoSource = (video: HTMLVideoElement, url: string) => {
  if (isHlsUrl(url) && Hls.isSupported() && !video.canPlayType('application/vnd.apple.mpegurl')) {
    const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
    hls.loadSource(url);
    hls.attachMedia(video);
    return () => hls.destroy();
  }

  video.src = url;
  return () => {
    video.removeAttribute('src');
    video.load();
  };
};

const loadImageAspect = (url: string) =>
  new Promise<number>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (!image.naturalWidth || !image.naturalHeight) {
        reject(new Error('Marker image has no dimensions'));
        return;
      }
      resolve(image.naturalWidth / image.naturalHeight);
    };
    image.onerror = () => reject(new Error('Marker image failed to load'));
    image.src = url;
  });

const fitTextureToAspect = (texture: THREE.Texture, sourceAspect: number, targetAspect: number) => {
  texture.center.set(0.5, 0.5);
  if (sourceAspect > targetAspect) {
    texture.repeat.set(targetAspect / sourceAspect, 1);
  } else {
    texture.repeat.set(1, sourceAspect / targetAspect);
  }
  texture.offset.set((1 - texture.repeat.x) / 2, (1 - texture.repeat.y) / 2);
  texture.needsUpdate = true;
};

const getBrowserSupport = (): BrowserSupport => {
  const ua = navigator.userAgent || '';
  const isSamsungInternet = /SamsungBrowser/i.test(ua);
  const isChrome =
    /(Chrome|CriOS)\/\d+/i.test(ua) &&
    !/(EdgA?|OPR|Opera|SamsungBrowser|DuckDuckGo|UCBrowser|YaBrowser|FxiOS)/i.test(ua);
  const isSafari =
    /Safari\/\d+/i.test(ua) &&
    /Version\/\d+/i.test(ua) &&
    !/(Chrome|CriOS|Chromium|Edg|OPR|Opera|SamsungBrowser|Firefox|FxiOS|DuckDuckGo)/i.test(ua);

  if (isChrome) return { supported: true, name: 'Chrome' };
  if (isSafari) return { supported: true, name: 'Safari' };
  if (isSamsungInternet) return { supported: false, name: 'Samsung Internet' };
  return { supported: false, name: 'your current browser' };
};

const buildChromeOpenUrl = (currentUrl: string) => {
  const isAndroid = /Android/i.test(navigator.userAgent || '');
  const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

  if (isAndroid) {
    const url = new URL(currentUrl);
    const pathWithQuery = `${url.pathname}${url.search}${url.hash}`;
    const fallback = encodeURIComponent(currentUrl);
    return `intent://${url.host}${pathWithQuery}#Intent;scheme=${url.protocol.replace(':', '')};package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
  }

  if (isIos) return currentUrl.replace(/^https:/i, 'googlechromes:').replace(/^http:/i, 'googlechrome:');
  return 'https://www.google.com/chrome/';
};

const styleMindArLayers = (mindarThree: InstanceType<MindARThreeConstructor>, container: HTMLElement) => {
  container.style.background = 'transparent';
  container.style.isolation = 'isolate';

  const cameraVideo = mindarThree.video;
  if (cameraVideo) {
    cameraVideo.muted = true;
    cameraVideo.playsInline = true;
    cameraVideo.setAttribute('playsinline', 'true');
    cameraVideo.setAttribute('webkit-playsinline', 'true');
    cameraVideo.style.height = '100%';
    cameraVideo.style.inset = '0';
    cameraVideo.style.maxHeight = 'none';
    cameraVideo.style.maxWidth = 'none';
    cameraVideo.style.minHeight = '100%';
    cameraVideo.style.minWidth = '100%';
    cameraVideo.style.objectFit = 'cover';
    cameraVideo.style.width = '100%';
    cameraVideo.style.zIndex = '0';
  }

  const canvas = mindarThree.renderer.domElement;
  mindarThree.renderer.setClearAlpha(0);
  canvas.style.background = 'transparent';
  canvas.style.inset = '0';
  canvas.style.zIndex = '1';
};

const isQrEntrySource = (source: string) => ['qr', 'qrcode', 'qr_code'].includes(source.toLowerCase());

const loadCollection = async (publicSlug: string) => {
  if (!supabase) throw new Error('Supabase is not configured');
  const { data, error } = await supabase.functions.invoke('get-public-scan-collection', {
    body: { publicSlug }
  });
  if (error) throw error;
  if (!data?.ok) throw new Error(data?.error ?? 'Scan collection unavailable');
  return data.collection as CollectionExperience;
};

const targetLabel = (target: CollectionTarget) => {
  const page = target.pageNumber ? `Page ${target.pageNumber}` : 'Magazine ad';
  return target.placementLabel ? `${page} - ${target.placementLabel}` : page;
};

export const CollectionScanPage = () => {
  const { collectionSlug = '' } = useParams();
  const browserSupport = useMemo(() => getBrowserSupport(), []);
  const chromeUrl = useMemo(() => buildChromeOpenUrl(window.location.href), []);
  const arContainerRef = useRef<HTMLDivElement | null>(null);
  const arRuntimeRef = useRef<CollectionArRuntime | null>(null);
  const arRunIdRef = useRef(0);
  const activeTargetRef = useRef<CollectionTarget | null>(null);
  const visibleStartedAtRef = useRef<number | null>(null);
  const playbackVideoRef = useRef<HTMLVideoElement | null>(null);
  const progressMarksRef = useRef(new Map<string, Set<number>>());
  const startedVideosRef = useRef(new Set<string>());
  const ctaImpressionsRef = useRef(new Set<string>());
  const landingEventTrackedRef = useRef('');
  const viewerLoadTrackedRef = useRef('');
  const [allowUnsupportedBrowser, setAllowUnsupportedBrowser] = useState(false);
  const [mode, setMode] = useState<'ready' | 'camera' | 'video'>('ready');
  const [cameraState, setCameraState] = useState<'idle' | 'starting' | 'active' | 'recognized' | 'denied' | 'error'>('idle');
  const [activeTarget, setActiveTarget] = useState<CollectionTarget | null>(null);
  const [showCtaOverlay, setShowCtaOverlay] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [playContext, setPlayContext] = useState<PlayContext>('direct');

  const collectionQuery = useQuery({
    queryKey: ['public-scan-collection', collectionSlug],
    queryFn: () => loadCollection(collectionSlug),
    enabled: Boolean(collectionSlug && hasSupabaseConfig && (browserSupport.supported || allowUnsupportedBrowser)),
    retry: 1
  });

  const collection = collectionQuery.data;
  const canUseCamera = typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
  const canTrack = Boolean(collection?.mindFileUrl && collection.targets.length);
  const entrySource = useMemo(() => {
    const search = new URLSearchParams(window.location.search);
    return search.get('source') || search.get('utm_source') || '';
  }, []);
  const isQrEntry = isQrEntrySource(entrySource);
  const ctaKicker = activeTarget?.ctaKicker?.trim() || '';
  const ctaHeadline = activeTarget?.ctaHeadline?.trim() || '';
  const ctaBody = activeTarget?.ctaBody?.trim() || '';
  const ctaButtonLabel = activeTarget?.ctaLabel?.replace(/\s*\(https?:\/\/[^)]+\)\s*$/i, '').trim() || ctaDefaults.label;
  const ctaThemeStyle = useMemo(
    () => ctaThemeCssVariables(activeTarget?.ctaTheme) as CSSProperties,
    [activeTarget?.ctaTheme]
  );

  const progressKeyFor = (target: CollectionTarget, context: PlayContext) => `${context}:${target.targetIndex}`;

  const trackVisibleDuration = () => {
    const target = activeTargetRef.current;
    const startedAt = visibleStartedAtRef.current;
    if (!target || !startedAt) return;
    const durationMs = Date.now() - startedAt;
    visibleStartedAtRef.current = null;
    if (durationMs >= 250) {
      void trackCollectionEvent(collectionSlug, 'target_visible_duration', { duration_ms: durationMs }, target.targetIndex);
    }
  };

  const recordVideoStarted = (target: CollectionTarget, context: PlayContext) => {
    const key = progressKeyFor(target, context);
    if (startedVideosRef.current.has(key)) return;
    startedVideosRef.current.add(key);
    void trackCollectionEvent(collectionSlug, 'video_started', { play_context: context }, target.targetIndex);
  };

  const recordProgress = (target: CollectionTarget, context: PlayContext, progress: number) => {
    const key = progressKeyFor(target, context);
    const marks = progressMarksRef.current.get(key) ?? new Set<number>();
    for (const mark of [25, 50, 75]) {
      if (progress >= mark && !marks.has(mark)) {
        marks.add(mark);
        void trackCollectionEvent(collectionSlug, 'video_progress', { progress, play_context: context }, target.targetIndex);
      }
    }
    progressMarksRef.current.set(key, marks);
  };

  const cleanupArRuntime = () => {
    trackVisibleDuration();
    const runtime = arRuntimeRef.current;
    arRuntimeRef.current = null;
    activeTargetRef.current = null;
    if (!runtime) return;

    for (const anchor of runtime.anchors) {
      anchor.onTargetFound = undefined;
      anchor.onTargetLost = undefined;
    }
    runtime.renderer.setAnimationLoop(null);
    runtime.video.pause();
    runtime.cleanupVideoSource();
    runtime.texture.dispose();
    runtime.mesh.geometry.dispose();
    runtime.mesh.material.dispose();
    try {
      void Promise.resolve(runtime.mindarThree.stop()).catch(() => undefined);
    } catch {
      // MindAR can throw if startup timed out before its controller was created.
    }
    runtime.container.replaceChildren();
  };

  const stopCamera = (keepTarget = false) => {
    arRunIdRef.current += 1;
    cleanupArRuntime();
    setCameraState('idle');
    setMode('ready');
    if (!keepTarget) setActiveTarget(null);
  };

  const openVideo = (target: CollectionTarget, context: PlayContext = 'direct') => {
    setPlayContext(context);
    stopCamera(true);
    setActiveTarget(target);
    activeTargetRef.current = target;
    setMode('video');
  };

  useEffect(() => {
    const landingEventKey = `${collectionSlug}:${entrySource || 'direct'}`;
    if (!collectionSlug || !hasSupabaseConfig || landingEventTrackedRef.current === landingEventKey) return;
    landingEventTrackedRef.current = landingEventKey;
    if (isQrEntry) void trackCollectionEvent(collectionSlug, 'qr_scan', { source: entrySource || 'qr' });
    void trackCollectionEvent(collectionSlug, 'landing_view', { source: entrySource || 'direct' });
  }, [collectionSlug, entrySource, isQrEntry]);

  useEffect(() => {
    if (!collection) return;
    const viewerLoadKey = `${collectionSlug}:${collection.publicSlug}`;
    if (viewerLoadTrackedRef.current === viewerLoadKey) return;
    viewerLoadTrackedRef.current = viewerLoadKey;
    void trackCollectionEvent(collectionSlug, 'viewer_loaded', {
      target_count: collection.targets.length,
      has_mind_file: Boolean(collection.mindFileUrl)
    });
  }, [collection, collectionSlug]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previous = {
      bodyBackground: body.style.background,
      htmlBackground: html.style.background,
      overscrollBehavior: body.style.overscrollBehavior
    };
    html.style.background = '#000210';
    body.style.background = '#000210';
    body.style.overscrollBehavior = 'none';
    return () => {
      html.style.background = previous.htmlBackground;
      body.style.background = previous.bodyBackground;
      body.style.overscrollBehavior = previous.overscrollBehavior;
    };
  }, []);

  useEffect(
    () => () => {
      cleanupArRuntime();
    },
    // Runtime cleanup should run only when the route unmounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') trackVisibleDuration();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  useEffect(() => {
    if (!activeTarget?.ctaUrl || mode === 'ready') {
      setShowCtaOverlay(false);
      return;
    }
    if (mode === 'camera' && cameraState !== 'recognized') return;

    setShowCtaOverlay(false);
    const timeoutId = window.setTimeout(() => setShowCtaOverlay(true), CTA_REVEAL_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [activeTarget, cameraState, mode]);

  useEffect(() => {
    if (!showCtaOverlay || !activeTarget?.ctaUrl) return;
    const key = `${activeTarget.targetIndex}:${activeTarget.ctaUrl}:${mode}`;
    if (ctaImpressionsRef.current.has(key)) return;
    ctaImpressionsRef.current.add(key);
    void trackCollectionEvent(
      collectionSlug,
      'cta_impression',
      {
        cta_label: ctaButtonLabel,
        cta_headline: ctaHeadline,
        display_mode: mode
      },
      activeTarget.targetIndex
    );
  }, [activeTarget, collectionSlug, ctaButtonLabel, ctaHeadline, mode, showCtaOverlay]);

  useEffect(() => {
    const runtime = arRuntimeRef.current;
    if (!runtime) return;
    runtime.video.muted = !soundEnabled;
    if (cameraState === 'recognized') {
      void runtime.video.play().catch(() => undefined);
    }
  }, [cameraState, soundEnabled]);

  useEffect(() => {
    if (mode !== 'camera' || !collection) return;
    const runId = arRunIdRef.current + 1;
    arRunIdRef.current = runId;

    const startAr = async () => {
      if (!canUseCamera || !canTrack || !collection.mindFileUrl) {
        setCameraState('error');
        return;
      }

      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      if (arRunIdRef.current !== runId) return;

      const container = arContainerRef.current;
      if (!container) return;

      try {
        cleanupArRuntime();
        const module = (await import(/* @vite-ignore */ MINDAR_THREE_MODULE_URL)) as {
          MindARThree: MindARThreeConstructor;
        };
        if (arRunIdRef.current !== runId) return;

        const mindarThree = new module.MindARThree({
          container,
          imageTargetSrc: collection.mindFileUrl,
          filterMinCF: 0.0001,
          filterBeta: 0.001,
          uiLoading: 'no',
          uiScanning: 'no'
        });
        const { renderer, scene, camera } = mindarThree;
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(container.clientWidth, container.clientHeight);

        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.loop = true;
        video.muted = !soundEnabled;
        video.playsInline = true;
        video.preload = 'auto';
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        const texture = new THREE.VideoTexture(video);
        texture.colorSpace = THREE.SRGBColorSpace;
        const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, transparent: true });
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 9 / 16), material);
        mesh.visible = false;
        let cleanupVideoSource: () => void = () => undefined;
        let currentVideoUrl = '';
        let currentTargetAspect = 16 / 9;

        video.addEventListener('loadedmetadata', () => {
          const sourceAspect = video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : currentTargetAspect;
          mesh.geometry.dispose();
          mesh.geometry = new THREE.PlaneGeometry(1, 1 / currentTargetAspect);
          fitTextureToAspect(texture, sourceAspect, currentTargetAspect);
        });
        video.addEventListener('timeupdate', () => {
          const target = activeTargetRef.current;
          if (!target || !video.duration || !Number.isFinite(video.duration)) return;
          recordProgress(target, 'ar', Math.floor((video.currentTime / video.duration) * 100));
        });
        video.addEventListener('ended', () => {
          const target = activeTargetRef.current;
          if (target) void trackCollectionEvent(collectionSlug, 'video_completed', { play_context: 'ar' }, target.targetIndex);
        });

        const activateTarget = async (target: CollectionTarget, anchor: CollectionAnchor) => {
          if (!target.videoUrl) return;
          if (activeTargetRef.current?.targetIndex !== target.targetIndex) trackVisibleDuration();
          activeTargetRef.current = target;
          visibleStartedAtRef.current = Date.now();
          setActiveTarget(target);
          setCameraState('recognized');
          void trackCollectionEvent(collectionSlug, 'image_recognized', { ad_name: target.adName }, target.targetIndex);
          if (target.markerUrl) {
            currentTargetAspect = await loadImageAspect(target.markerUrl).catch(() => 16 / 9);
            mesh.geometry.dispose();
            mesh.geometry = new THREE.PlaneGeometry(1, 1 / currentTargetAspect);
          }
          if (currentVideoUrl !== target.videoUrl) {
            video.pause();
            cleanupVideoSource();
            cleanupVideoSource = attachVideoSource(video, target.videoUrl);
            currentVideoUrl = target.videoUrl;
          }
          anchor.group.add(mesh);
          mesh.visible = true;
          recordVideoStarted(target, 'ar');
          void video.play().catch(() => undefined);
        };

        const anchors = collection.targets.map((target) => {
          const anchor = mindarThree.addAnchor(target.targetIndex);
          anchor.onTargetFound = () => {
            void activateTarget(target, anchor);
          };
          anchor.onTargetLost = () => {
            if (activeTargetRef.current?.targetIndex !== target.targetIndex) return;
            trackVisibleDuration();
            activeTargetRef.current = null;
            setActiveTarget(null);
            setCameraState('active');
            mesh.visible = false;
            video.pause();
          };
          return anchor;
        });

        arRuntimeRef.current = {
          anchors,
          cleanupVideoSource: () => cleanupVideoSource(),
          container,
          mesh,
          mindarThree,
          renderer,
          texture,
          video
        };

        const startPromise = mindarThree.start();
        let timeoutId = 0;
        await Promise.race([
          startPromise,
          new Promise((_, reject) => {
            timeoutId = window.setTimeout(() => reject(new Error('AR_START_TIMEOUT')), AR_START_TIMEOUT_MS);
          })
        ]).finally(() => window.clearTimeout(timeoutId));

        if (arRunIdRef.current !== runId) return;
        styleMindArLayers(mindarThree, container);
        setCameraState('active');
        void trackCollectionEvent(collectionSlug, 'ar_started', {
          mode: 'mindar_multi_image_tracking',
          target_count: collection.targets.length
        });
        renderer.setAnimationLoop(() => renderer.render(scene, camera));
      } catch (error) {
        console.warn('[collection-scan] MindAR start failed', error);
        cleanupArRuntime();
        const name = error instanceof DOMException ? error.name : '';
        setCameraState(name === 'NotAllowedError' ? 'denied' : 'error');
        if (name === 'NotAllowedError') void trackCollectionEvent(collectionSlug, 'camera_permission_denied');
        else void trackCollectionEvent(collectionSlug, 'error', { reason: error instanceof Error ? error.message : 'ar_start_failed' });
      }
    };

    void startAr();
    return () => {
      arRunIdRef.current += 1;
      cleanupArRuntime();
    };
    // Tracking should start once when entering camera mode. Runtime sound is handled separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canTrack, canUseCamera, collection, collectionSlug, mode]);

  const startCamera = async () => {
    if (!collection) return;
    if (!canUseCamera || !canTrack) {
      setCameraState('error');
      setMode('camera');
      return;
    }
    setMode('camera');
    setCameraState('starting');
    void trackCollectionEvent(collectionSlug, 'camera_opened', { target_count: collection.targets.length });
  };

  const onVideoTimeUpdate = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (!activeTarget) return;
    const video = event.currentTarget;
    if (!video.duration || !Number.isFinite(video.duration)) return;
    recordProgress(activeTarget, playContext, Math.floor((video.currentTime / video.duration) * 100));
  };

  if (!browserSupport.supported && !allowUnsupportedBrowser) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#000210] px-5 text-white">
        <section className="max-w-lg rounded-lg border border-white/14 bg-white/8 p-6">
          <p className="text-sm font-semibold uppercase text-blue-200">Best in Chrome or Safari</p>
          <h1 className="mt-3 font-heading text-4xl font-semibold">Open in Chrome for the best camera experience</h1>
          <p className="mt-4 leading-7 text-white/70">
            This Scan Collection works best in Chrome or Safari. You are currently using {browserSupport.name}, which may
            block the camera.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <a className="rounded-md bg-primary px-4 py-3 text-center font-semibold" href={chromeUrl}>
              Open with Chrome
            </a>
            <button className="rounded-md border border-white/18 px-4 py-3 font-semibold" onClick={() => setAllowUnsupportedBrowser(true)} type="button">
              Continue anyway
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="scan-page text-white">
      {collectionQuery.isLoading && (
        <section className="scan-page-fill flex items-center justify-center text-white/72">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Opening this Scan Collection...
        </section>
      )}

      {collectionQuery.isError && (
        <section className="mx-auto max-w-xl px-5 py-16">
          <div className="rounded-lg border border-white/14 bg-white/8 p-6">
            <p className="text-sm font-semibold uppercase text-red-200">Link unavailable</p>
            <h1 className="mt-3 font-heading text-4xl font-semibold">This Scan Collection is not ready yet</h1>
            <p className="mt-4 text-white/70">Please try again soon.</p>
          </div>
        </section>
      )}

      {collection && (
        <section className="scan-page-stage" onClick={() => mode === 'ready' && void startCamera()}>
          {mode === 'camera' && canTrack && cameraState !== 'error' && (
            <div ref={arContainerRef} className="absolute inset-0 h-full w-full overflow-hidden bg-transparent" />
          )}

          {mode === 'video' && activeTarget?.videoUrl && (
            <video
              ref={playbackVideoRef}
              autoPlay
              className="absolute inset-0 h-full w-full bg-black object-contain"
              controls
              loop
              muted={!soundEnabled}
              onEnded={() => void trackCollectionEvent(collectionSlug, 'video_completed', { play_context: playContext }, activeTarget.targetIndex)}
              onPlay={() => recordVideoStarted(activeTarget, playContext)}
              onTimeUpdate={onVideoTimeUpdate}
              playsInline
              src={activeTarget.videoUrl}
            />
          )}

          <div className={`absolute inset-0 ${mode === 'camera' && cameraState === 'recognized' ? 'scan-camera-shade-clear' : 'scan-ready-shade'}`} />

          <div className="scan-top-actions">
            <button
              aria-label={soundEnabled ? 'Mute sound' : 'Unmute sound'}
              className="scan-icon-button scan-sound-button"
              onClick={(event) => {
                event.stopPropagation();
                setSoundEnabled((enabled) => !enabled);
              }}
              type="button"
            >
              {soundEnabled ? <Volume2 className="h-6 w-6" /> : <VolumeX className="h-6 w-6" />}
            </button>
            {mode !== 'ready' && (
              <button
                aria-label="Close"
                className="scan-icon-button scan-settings-button"
                onClick={(event) => {
                  event.stopPropagation();
                  stopCamera();
                }}
                type="button"
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>

          {mode !== 'video' && (
            <div className={`scan-reticle-layer ${cameraState === 'recognized' ? 'scan-reticle-layer-hidden' : 'scan-reticle-layer-visible'}`}>
              <div className="scan-reticle" aria-hidden="true">
                <span className="scan-corner scan-corner-tl" />
                <span className="scan-corner scan-corner-tr" />
                <span className="scan-corner scan-corner-bl" />
                <span className="scan-corner scan-corner-br" />
                <span className="scan-sweep" />
                <span className="scan-pulse" />
                <Sparkles className="scan-center-glyph" />
              </div>
            </div>
          )}

          <div className="scan-page-fill relative z-30 flex flex-col items-center justify-end px-5 pb-[calc(5rem+env(safe-area-inset-bottom))] pt-36 text-center">
            {mode === 'ready' && (
              <div className="scan-ready-content">
                <h1 className="scan-ready-title">
                  Scan this
                  <br />
                  issue <span>once</span>
                </h1>
                <p className="scan-ready-copy">Point your camera at any enabled ad in {collection.name}.</p>
                <button className="scan-start-pill" disabled={!collection.targets.length} onClick={(event) => {
                  event.stopPropagation();
                  void startCamera();
                }} type="button">
                  <Sparkles className="h-5 w-5" />
                  Start scanning
                </button>
              </div>
            )}

            {mode === 'camera' && ['starting', 'denied', 'error'].includes(cameraState) && (
              <div className="scan-status-card">
                {cameraState === 'starting' && (
                  <p className="flex items-center gap-2 text-white/78">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Starting collection tracking...
                  </p>
                )}
                {cameraState === 'denied' && (
                  <p className="flex items-center gap-2 font-semibold text-red-100">
                    <CircleAlert className="h-4 w-4" />
                    Camera permission denied
                  </p>
                )}
                {cameraState === 'error' && (
                  <p className="flex items-center gap-2 font-semibold text-red-100">
                    <CircleAlert className="h-4 w-4" />
                    Image tracking could not start
                  </p>
                )}
              </div>
            )}
          </div>

          {mode === 'camera' && cameraState !== 'recognized' && (
            <div className="absolute inset-x-4 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-30 mx-auto max-w-xl rounded-lg border border-white/14 bg-black/55 p-4 backdrop-blur">
              <p className="text-sm font-semibold text-white/80">Ads in this Scan Collection</p>
              <div className="mt-3 grid max-h-48 gap-2 overflow-auto">
                {collection.targets.map((target) => (
                  <button
                    className="flex items-center justify-between gap-3 rounded-md border border-white/12 bg-white/8 px-3 py-2 text-left text-sm"
                    key={target.itemId}
                    onClick={(event) => {
                      event.stopPropagation();
                      openVideo(target, ['denied', 'error'].includes(cameraState) ? 'fallback' : 'direct');
                    }}
                    type="button"
                  >
                    <span>
                      <span className="block font-semibold">{target.adName}</span>
                      <span className="text-white/58">{targetLabel(target)}</span>
                    </span>
                    <Play className="h-4 w-4 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === 'camera' && (
            <p className="absolute inset-x-0 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-30 text-center text-sm text-white/44">
              Powered by <span className="text-white/70">ADarveo</span>
            </p>
          )}

          {showCtaOverlay && activeTarget?.ctaUrl && (
            <div className="scan-cta-shell">
              <section
                aria-label={ctaHeadline || ctaButtonLabel || 'Call to action'}
                className="scan-cta-card"
                style={ctaThemeStyle}
              >
                <button aria-label="Close" className="scan-cta-close" onClick={() => setShowCtaOverlay(false)} type="button">
                  <X className="h-5 w-5" />
                </button>

                <div className="scan-cta-layout">
                  <div className="scan-cta-spark" aria-hidden="true">
                    {activeTarget.ctaLogoUrl ? (
                      <img alt="" className="scan-cta-logo" src={activeTarget.ctaLogoUrl} />
                    ) : (
                      <Sparkles className="h-5 w-5" />
                    )}
                  </div>
                  <div className="scan-cta-copy">
                    {ctaKicker && <p className="scan-cta-kicker">{ctaKicker}</p>}
                    {ctaHeadline && <h2>{ctaHeadline}</h2>}
                    {ctaBody && <p className="scan-cta-body">{ctaBody}</p>}
                  </div>
                </div>

                <a
                  className="scan-cta-link"
                  href={activeTarget.ctaUrl}
                  onClick={() => void trackCollectionEvent(collectionSlug, 'cta_clicked', { action: 'primary_cta' }, activeTarget.targetIndex)}
                  rel="noreferrer"
                  target="_blank"
                >
                  {ctaButtonLabel}
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </section>
            </div>
          )}
        </section>
      )}
    </main>
  );
};
